/**
 * showdownFx — a Phaser port of Pokémon Showdown's battle-animation engine.
 *
 * Showdown describes a move as an imperative function that fires a sequence of
 * `scene.showEffect(...)` calls (sprites flying around) and `actor.anim(...)`
 * calls (the combatant sprites lunging), all scheduled on a shared `timeOffset`
 * clock advanced by `scene.wait(ms)`. The ~600 ported move scripts call exactly
 * that API. This file provides a Phaser-backed implementation of it:
 *
 *   - {@link FxScene}        — the `scene` argument: showEffect / backgroundEffect /
 *                              wait, plus the catalog + the shared time clock.
 *   - {@link Actor}          — a combatant: a screen anchor, the facing helpers
 *                              (behind / leftof / behindx / behindy), `anim()` that
 *                              tweens the *combatant sprite*, and `delay()`.
 *   - {@link playShowdownAnim} — runs one MoveAnim function to completion (honouring
 *                              `pace` time-scaling and a `reduced`-motion fast path)
 *                              and resolves when every spawned effect has finished.
 *
 * All coordinate / timing / easing math lives in the PURE, unit-tested
 * `showdownFx.coords.ts`; this file is the Phaser drawing + scheduling half only.
 *
 * Teardown discipline is borrowed verbatim from `moveEffects.ts`: every Image /
 * Tween / Timer is tracked and swept on completion, and a SHUTDOWN/DESTROY guard
 * guarantees cleanup + prompt resolution if the scene dies mid-animation.
 *
 * Asset note: the fx PNGs are Game-Freak-derived art, vendored locally and used
 * for personal, non-distributed purposes only (same IP boundary as our other
 * battle sprites). The engine + ported scripts are MIT/CC0.
 */
import Phaser from 'phaser';
import {
  inheritEnd,
  showdownPos,
  easeFor,
  isBallistic,
  ballisticArc,
  resolve,
  fitFieldToAnchors,
  NEAR_Z,
  FAR_Z,
  behind as logicBehind,
  behindx as logicBehindx,
  behindy as logicBehindy,
  leftof as logicLeftof,
  type ScenePos,
  type ResolvedScenePos,
  type FxSize,
  type FieldTransform,
  type ScreenRect,
  type ActorAnchor,
} from './showdownFx.coords.js';

export type { ScenePos } from './showdownFx.coords.js';
export { NEAR_Z, FAR_Z } from './showdownFx.coords.js';

/** One vendored fx entry (matches client/data/generated/fx/catalog.json). */
export interface FxCatalogEntry {
  key: string;
  file: string;
  w: number;
  h: number;
  pixelated?: boolean;
}
export type FxCatalog = Record<string, FxCatalogEntry>;

/** The base URL the fx PNGs are served from at runtime. */
export const FX_URL_BASE = '/generated/fx';

/** Default render depth for spawned effect images (above the combatant sprites). */
const DEFAULT_DEPTH = 1200;

/** PS `after` fade duration / explode duration (ms), ported verbatim. */
const FADE_MS = 100;
const EXPLODE_MS = 200;
const EXPLODE_SCALE = 3;

/** Showdown's default anim duration when an anim/effect omits `time`. */
const DEFAULT_ANIM_MS = 500;

/**
 * A MoveAnim is the ported Showdown script. It is handed the {@link FxScene} and a
 * tuple of {@link Actor}s ([attacker, defender] in the common 1v1 case) and fires
 * showEffect / actor.anim / scene.wait calls. It returns nothing; completion is
 * tracked by the FxScene. (Mirrors PS `AnimData.anim(scene, args)`.)
 */
export type MoveAnim = (scene: FxScene, actors: Actor[]) => void;

/** The shape a ported move table entry takes (mirrors PS `AnimData`). */
export interface MoveAnimEntry {
  anim: MoveAnim;
  /** Optional pre-roll (charge) animation; same signature. */
  prepareAnim?: MoveAnim;
  /** Optional residual (end-of-turn) animation; same signature. */
  residualAnim?: MoveAnim;
}
export type MoveAnimTable = Record<string, MoveAnimEntry>;

/** Options for {@link playShowdownAnim}. */
export interface PlayShowdownOptions {
  /** Duration multiplier (turbo < 1 = faster, > 1 = slower). Default 1. */
  pace?: number;
  /** Reduced-motion: skip the choreography, play a brief gentle cue. Default false. */
  reduced?: boolean;
  /** The fx catalog (defaults to the module-loaded one). */
  catalog?: FxCatalog;
  /** Render depth for spawned images. */
  depth?: number;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Catalog loading
 * ──────────────────────────────────────────────────────────────────────── */

let cachedCatalog: FxCatalog | null = null;

/** Set the active fx catalog (call once after loading catalog.json). */
export function setFxCatalog(catalog: FxCatalog): void {
  cachedCatalog = catalog;
}

/** The active fx catalog (empty until {@link setFxCatalog} / {@link preloadFx}). */
export function getFxCatalog(): FxCatalog {
  return cachedCatalog ?? {};
}

/**
 * Fetch + register the fx catalog and queue every PNG into a scene's loader. Call
 * from a scene's `preload()`. Texture keys == catalog keys == effect names, so
 * `showEffect(name, ...)` can use the name directly as the texture key.
 *
 * Resolves once the catalog JSON is parsed (the loader runs on `scene.load.start`
 * / the scene's normal preload pump). Safe to call repeatedly.
 */
export async function preloadFx(
  scene: Phaser.Scene,
  catalogUrl = `${FX_URL_BASE}/catalog.json`,
): Promise<FxCatalog> {
  if (!cachedCatalog) {
    const res = await fetch(catalogUrl);
    cachedCatalog = (await res.json()) as FxCatalog;
  }
  for (const entry of Object.values(cachedCatalog)) {
    if (!scene.textures.exists(entry.key)) {
      scene.load.image(entry.key, `${FX_URL_BASE}/${entry.file}`);
    }
  }
  return cachedCatalog;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Run tracker (crash-safe teardown — modelled on moveEffects.ts EffectRun)
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Owns every object/tween/timer a single animation run spawns, so teardown is a
 * single sweep. A SHUTDOWN/DESTROY listener guarantees cleanup + resolution even
 * if the scene dies mid-animation. The {@link FxScene} delegates all spawning here.
 */
class FxRun {
  readonly objects: Phaser.GameObjects.GameObject[] = [];
  readonly tweens: Phaser.Tweens.Tween[] = [];
  readonly timers: Phaser.Time.TimerEvent[] = [];
  private done = false;
  /** Count of in-flight animations; the run resolves when this hits 0 and `sealed`. */
  private pending = 0;
  private sealed = false;
  private resolveRun: (() => void) | null = null;
  private readonly onShutdown = (): void => this.abort();

  constructor(readonly scene: Phaser.Scene) {
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown);
    scene.events.once(Phaser.Scenes.Events.DESTROY, this.onShutdown);
  }

  /** True only while the scene is alive and this run hasn't been torn down. */
  get alive(): boolean {
    return !this.done && !!this.scene.sys && this.scene.sys.isActive();
  }

  /**
   * Register ONE piece of in-flight work — a tween, a scheduled delayedCall, a
   * wait, or any after-effect tail. Anything that will resolve the run later MUST
   * call this synchronously *at schedule time* so the run cannot resolve in the
   * window before deferred work fires. (This is the fix for the destroy-before-fire
   * race: showEffect schedules its reveal inside a delayedCall, so the delayedCall
   * itself is tracked the instant it is queued — see {@link delayedCall}.)
   */
  track(): void {
    this.pending++;
  }

  /** Mark one piece of in-flight work finished; resolve the run if all are done. */
  untrack(): void {
    // Guard against post-teardown / double untracks (e.g. a settle racing abort).
    if (this.done) return;
    this.pending--;
    this.maybeResolve();
  }

  /** No more work will be scheduled; resolve as soon as in-flight work drains. */
  seal(): void {
    this.sealed = true;
    this.maybeResolve();
  }

  private maybeResolve(): void {
    if (this.sealed && this.pending <= 0) this.finish();
  }

  /** A tracked Image (cleaned at teardown). */
  addImage(key: string): Phaser.GameObjects.Image | null {
    if (!this.alive || !this.scene.textures.exists(key)) return null;
    const img = this.scene.add.image(0, 0, key);
    this.objects.push(img);
    return img;
  }

  /** A tracked rectangle (for backgroundEffect). */
  addRect(
    x: number,
    y: number,
    w: number,
    h: number,
    color: number,
  ): Phaser.GameObjects.Rectangle | null {
    if (!this.alive) return null;
    const r = this.scene.add.rectangle(x, y, w, h, color);
    this.objects.push(r);
    return r;
  }

  /**
   * A tracked tween. Returns a promise that resolves on complete OR on teardown,
   * and participates in the run's pending count via track/untrack.
   */
  tween(config: Phaser.Types.Tweens.TweenBuilderConfig): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!this.alive) {
        resolve();
        return;
      }
      this.track();
      let settled = false;
      const settle = (): void => {
        if (settled) return;
        settled = true;
        this.untrack();
        resolve();
      };
      const tw = this.scene.tweens.add({
        ...config,
        onComplete: () => {
          config.onComplete?.(tw, config.targets as object[]);
          settle();
        },
        onStop: () => settle(),
      });
      this.tweens.push(tw);
    });
  }

  /**
   * A tracked delayed call. Counts as pending FROM THE MOMENT IT IS SCHEDULED
   * (synchronously, before the timer fires) until its callback has fully run — so
   * a run that has scheduled deferred effects (e.g. showEffect's reveal+tween) can
   * never resolve/tear down before they fire. The pending count is only released
   * after `fn` returns; because `fn` itself synchronously tracks any further work
   * it kicks off (its tween/after-tail), the count never dips to 0 across the
   * hand-off, so the run stays alive for the whole effect chain.
   */
  delayedCall(ms: number, fn: () => void): Phaser.Time.TimerEvent | null {
    if (!this.alive) return null;
    this.track();
    let released = false;
    const release = (): void => {
      if (released) return;
      released = true;
      this.untrack();
    };
    const t = this.scene.time.delayedCall(Math.max(1, ms), () => {
      try {
        if (this.alive) fn();
      } finally {
        release();
      }
    });
    this.timers.push(t);
    return t;
  }

  /**
   * A pace-aware wait promise tied to this run. Tracked from scheduling until the
   * timer fires (or until teardown), so a wait inserted between two tween phases
   * (e.g. backgroundEffect's fade-in → hold → fade-out) keeps the run pending and
   * prevents a premature resolve in the gap.
   */
  wait(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!this.alive || ms <= 0) {
        resolve();
        return;
      }
      this.track();
      let settled = false;
      const settle = (): void => {
        if (settled) return;
        settled = true;
        this.untrack();
        resolve();
      };
      const t = this.scene.time.delayedCall(Math.max(1, ms), settle);
      this.timers.push(t);
    });
  }

  /** Begin the run; resolves once sealed + all anims complete (or on teardown). */
  begin(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.resolveRun = resolve;
      if (!this.alive) {
        this.finish();
      }
    });
  }

  private finish(): void {
    if (this.done) return;
    const r = this.resolveRun;
    this.resolveRun = null;
    this.destroy();
    if (r) r();
  }

  private abort(): void {
    this.finish();
  }

  /** Destroy every object/tween/timer this run created. Idempotent + defensive. */
  destroy(): void {
    if (this.done) return;
    this.done = true;
    try {
      this.scene.events?.off(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown);
      this.scene.events?.off(Phaser.Scenes.Events.DESTROY, this.onShutdown);
    } catch {
      /* scene emitter gone — fine */
    }
    for (const tw of this.tweens) {
      try {
        tw.remove();
      } catch {
        /* already gone */
      }
    }
    for (const t of this.timers) {
      try {
        t.remove(false);
      } catch {
        /* already gone */
      }
    }
    for (const o of this.objects) {
      try {
        if (o.active) o.destroy();
      } catch {
        /* already gone */
      }
    }
    this.tweens.length = 0;
    this.timers.length = 0;
    this.objects.length = 0;
  }
}

/* ──────────────────────────────────────────────────────────────────────────
 * Actor — one combatant
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * A combatant in the animation. Carries its LOGICAL Showdown anchor (x/y/z, the
 * values the ported scripts read as `attacker.x` etc.) plus the live Phaser sprite
 * to lunge via {@link anim}. The facing helpers return logical coordinates (the
 * scripts feed them back through showEffect), exactly like PS's `Sprite`.
 */
export class Actor implements ActorAnchor {
  /** Logical Showdown x (− left / + right). */
  x: number;
  /** Logical Showdown y (− down / + up). */
  y: number;
  /** Logical Showdown z (− player / + opponent). */
  z: number;
  /** true = opponent (far) side; false = player (near) side. Drives facing signs. */
  readonly isFrontSprite: boolean;

  /** @internal set by the FxScene that owns this run. */
  private run: FxRun | null = null;
  /** @internal pace + field transform, injected by the FxScene. */
  private pace = 1;
  private field: FieldTransform = { U: 1, originX: 0, originY: 0 };

  constructor(
    /** The live combatant sprite this actor lunges. May be null (no-op anim). */
    readonly sprite: Phaser.GameObjects.Components.Transform | null,
    pos: { x: number; y: number; z: number; isFrontSprite: boolean },
  ) {
    this.x = pos.x;
    this.y = pos.y;
    this.z = pos.z;
    this.isFrontSprite = pos.isFrontSprite;
  }

  /** @internal wire this actor to its run + transform + pace. */
  bind(run: FxRun, field: FieldTransform, pace: number): void {
    this.run = run;
    this.field = field;
    this.pace = pace;
  }

  // ── Facing helpers (logical coords, per-side sign) ──────────────────────────
  behindx(offset: number): number {
    return logicBehindx(this, offset);
  }
  behindy(offset: number): number {
    return logicBehindy(this, offset);
  }
  leftof(offset: number): number {
    return logicLeftof(this, offset);
  }
  behind(offset: number): number {
    return logicBehind(this, offset);
  }

  /**
   * The combatant sprite's current SCREEN anchor (centre), derived from its logical
   * x/y/z through the same transform effects use. Useful for callers that need to
   * place something on the sprite without an anim.
   */
  screenAnchor(size: FxSize = { w: 0, h: 0 }): ScreenRect {
    return showdownPos(resolve({ x: this.x, y: this.y, z: this.z }), size, this.field);
  }

  /**
   * Lunge the COMBATANT sprite to a new logical position (PS `Sprite.anim`). Omitted
   * fields default to the actor's current x/y/z (so `anim({ z: ... })` moves only z).
   * Tweens the sprite's screen x/y over `time` (default 500), pace-scaled, with the
   * transition's easing (ballistic transitions add a parabolic Y arc).
   *
   * The actor's logical x/y/z are NOT permanently advanced — like PS, scripts read
   * the original anchor for every call; transient lunges return on their own anims.
   */
  anim(end: ScenePos, transition?: string): this {
    const run = this.run;
    const spr = this.sprite;
    if (!run || !run.alive || !spr) return this;

    const endTime = end.time ?? DEFAULT_ANIM_MS;
    const dur = Math.max(1, endTime * this.pace);

    const fromLoc: ResolvedScenePos = resolve({ x: this.x, y: this.y, z: this.z });
    // PS spreads {x,y,z,scale,opacity, ...end} over the current anchor.
    const toLoc: ResolvedScenePos = resolve({
      x: end.x ?? this.x,
      y: end.y ?? this.y,
      z: end.z ?? this.z,
      scale: end.scale ?? 1,
      xscale: end.xscale,
      yscale: end.yscale,
      opacity: end.opacity ?? 1,
    });

    const size: FxSize = { w: 0, h: 0 };
    const fromS = showdownPos(fromLoc, size, this.field);
    const toS = showdownPos(toLoc, size, this.field);

    this.tweenSprite(spr, fromS, toS, dur, transition);
    return this;
  }

  /** Delay this actor's next anim by `ms` (pace-scaled). Best-effort. */
  delay(ms: number): this {
    // We can't queue on a raw transform; record as a wait on the shared clock via
    // the run. Callers typically use scene.wait for ordering, so this is a soft no-op
    // beyond advancing nothing — kept for API parity with PS Sprite.delay.
    void ms;
    return this;
  }

  private tweenSprite(
    spr: Phaser.GameObjects.Components.Transform,
    from: ScreenRect,
    to: ScreenRect,
    dur: number,
    transition?: string,
  ): void {
    if (!this.run) return;
    const ballistic = isBallistic(transition);
    const ease = easeFor(transition);

    // Seed the start position so the tween reads as a deliberate move.
    spr.x = from.x;
    spr.y = from.y;

    if (!ballistic) {
      void this.run.tween({
        targets: spr,
        x: to.x,
        y: to.y,
        duration: dur,
        ease,
      });
      return;
    }

    // Ballistic: tween X linearly-eased; drive Y as a base lerp + parabolic arc.
    const arcHeight = -Math.abs(to.y - from.y) - 24; // jump up (screen −y)
    const proxy = { t: 0 };
    void this.run.tween({
      targets: proxy,
      t: 1,
      duration: dur,
      ease,
      onUpdate: () => {
        if (!spr || !(spr as unknown as { active?: boolean }).active) return;
        const t = proxy.t;
        spr.x = from.x + (to.x - from.x) * t;
        spr.y = from.y + (to.y - from.y) * t + ballisticArc(t, arcHeight);
      },
    });
  }
}

/* ──────────────────────────────────────────────────────────────────────────
 * FxScene — the `scene` arg to a MoveAnim
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * The animation context passed to a {@link MoveAnim}. Mirrors the slice of PS's
 * `BattleScene` the move scripts use: `showEffect`, `backgroundEffect`, `wait`,
 * plus the shared `timeOffset` clock that `wait` advances. Construct it via
 * {@link playShowdownAnim}; scripts never build it themselves.
 */
export class FxScene {
  /** Shared scheduling clock (ms). `wait(ms)` advances it; showEffect adds it to times. */
  timeOffset = 0;

  constructor(
    private readonly run: FxRun,
    private readonly catalog: FxCatalog,
    private readonly field: FieldTransform,
    private readonly pace: number,
    private readonly depth: number,
  ) {}

  /** Advance the shared clock so subsequent effects/anims start later (PS `wait`). */
  wait(time: number): void {
    this.timeOffset += time;
  }

  /**
   * Spawn an fx image and animate it start→end (PS `showEffect`). `name` is the
   * catalog/effect key (also the Phaser texture key). `transition` picks the easing
   * (ballistic = parabolic). `after` is the tail behaviour: 'fade' (opacity→0 over
   * 100ms) or 'explode' (scale ×3 + opacity→0 over 200ms); both then destroy the
   * image. Returns immediately; completion is tracked by the run.
   */
  showEffect(
    name: string,
    start: ScenePos,
    end: ScenePos,
    transition?: string,
    after?: string,
  ): void {
    const entry = this.catalog[name];
    if (!entry) return; // missing fx — skip gracefully (gaps logged at ingest)

    const img = this.run.addImage(entry.key);
    if (!img) return;
    img.setDepth(this.depth);

    const obj: FxSize = { w: entry.w, h: entry.h };

    // PS field inheritance + defaults (pure).
    const { start: s, end: e } = inheritEnd(start, end);

    // Pace-scale the local times, then add the shared offset.
    const startMs = (s.time + this.timeOffset) * this.pace;
    const endMs = (e.time + this.timeOffset) * this.pace;
    const dur = Math.max(1, endMs - startMs);

    const fromS = showdownPos(s, obj, this.field);
    const toS = showdownPos(e, obj, this.field);

    // Seat at start (hidden until startMs, like PS's opacity:0 + delay).
    img.setPosition(fromS.x, fromS.y);
    img.setDisplaySize(Math.max(0.01, fromS.w), Math.max(0.01, fromS.h));
    img.setAlpha(0);

    const ballistic = isBallistic(transition);
    const ease = easeFor(transition);

    // Phase 1: after the start delay, reveal + tween to end.
    this.run.delayedCall(startMs, () => {
      if (!img.active) return;
      img.setAlpha(s.opacity);
      this.tweenImage(img, fromS, toS, dur, ease, ballistic, () => {
        this.applyAfter(img, e, obj, after);
      });
    });
  }

  /** PS `backgroundEffect` — a full-field colour wash that fades in, holds, fades out. */
  backgroundEffect(css: string, durMs: number, opacity = 1, delay = 0): void {
    const cam = this.run.scene.cameras?.main;
    const w = cam?.width ?? 800;
    const h = cam?.height ?? 600;
    const cx = (cam?.scrollX ?? 0) + w / 2;
    const cy = (cam?.scrollY ?? 0) + h / 2;
    const color = parseCssColor(css);
    const rect = this.run.addRect(cx, cy, w, h, color);
    if (!rect) return;
    rect.setDepth(this.depth - 1);
    rect.setAlpha(0);

    const startMs = (delay + this.timeOffset) * this.pace;
    const fadeIn = Math.min(250, durMs) * this.pace;
    const hold = Math.max(0, durMs - 250) * this.pace;
    this.run.delayedCall(startMs, () => {
      if (!rect.active) return;
      void this.run
        .tween({ targets: rect, alpha: opacity, duration: Math.max(1, fadeIn), ease: 'Linear' })
        .then(() => this.run.wait(hold))
        .then(() =>
          this.run.tween({
            targets: rect,
            alpha: 0,
            duration: Math.max(1, 250 * this.pace),
            ease: 'Linear',
            onComplete: () => {
              if (rect.active) rect.destroy();
            },
          }),
        );
    });
  }

  private tweenImage(
    img: Phaser.GameObjects.Image,
    from: ScreenRect,
    to: ScreenRect,
    dur: number,
    ease: string,
    ballistic: boolean,
    onDone: () => void,
  ): void {
    const sizeChanges = from.w !== to.w || from.h !== to.h;
    // Ballistic transitions only arc the VERTICAL (PS `top`) channel; X stays a
    // plain interpolation. The arc peaks above the straight line (screen −y).
    const arcHeight = ballistic ? -Math.abs(to.y - from.y) - 24 : 0;
    const proxy = { t: 0 };
    void this.run.tween({
      targets: proxy,
      t: 1,
      duration: dur,
      ease,
      onUpdate: () => {
        if (!img.active) return;
        const t = proxy.t;
        img.x = from.x + (to.x - from.x) * t;
        img.y = from.y + (to.y - from.y) * t + (ballistic ? ballisticArc(t, arcHeight) : 0);
        if (sizeChanges) {
          img.setDisplaySize(
            Math.max(0.01, from.w + (to.w - from.w) * t),
            Math.max(0.01, from.h + (to.h - from.h) * t),
          );
        }
        img.setAlpha(from.opacity + (to.opacity - from.opacity) * t);
      },
      onComplete: () => onDone(),
    });
  }

  /** Apply PS's `after` tail behaviour, then destroy the image. */
  private applyAfter(
    img: Phaser.GameObjects.Image,
    end: ResolvedScenePos,
    obj: FxSize,
    after?: string,
  ): void {
    if (!img.active) {
      return;
    }
    if (after === 'fade') {
      void this.run
        .tween({
          targets: img,
          alpha: 0,
          duration: Math.max(1, FADE_MS * this.pace),
          ease: 'Linear',
        })
        .then(() => {
          if (img.active) img.destroy();
        });
      return;
    }
    if (after === 'explode') {
      const endLoc: ResolvedScenePos = {
        ...end,
        scale: end.scale * EXPLODE_SCALE,
        xscale: end.xscale * EXPLODE_SCALE,
        yscale: end.yscale * EXPLODE_SCALE,
        opacity: 0,
      };
      const target = showdownPos(endLoc, obj, this.field);
      void this.run
        .tween({
          targets: img,
          displayWidth: Math.max(0.01, target.w),
          displayHeight: Math.max(0.01, target.h),
          alpha: 0,
          duration: Math.max(1, EXPLODE_MS * this.pace),
          ease: 'Linear',
        })
        .then(() => {
          if (img.active) img.destroy();
        });
      return;
    }
    // No tail: destroy now.
    if (img.active) img.destroy();
  }

  /** @internal expose run/field/pace so playShowdownAnim can bind actors. */
  get _internals(): { run: FxRun; field: FieldTransform; pace: number } {
    return { run: this.run, field: this.field, pace: this.pace };
  }
}

/* ──────────────────────────────────────────────────────────────────────────
 * playShowdownAnim — the public entry
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Run a Showdown {@link MoveAnim} to full completion and resolve. `actors` is the
 * combatant tuple ([attacker, defender]); each must already carry its logical
 * anchor + sprite + isFrontSprite. `pace` scales every time in the animation;
 * `reduced` collapses the choreography to a brief gentle cue (a soft impact at the
 * defender) for accessibility / turbo. Never rejects; if the scene dies it tears
 * down and resolves promptly.
 *
 * Field transform: derived by {@link deriveFieldFromActors}, which FITS the two
 * actors' DISTINCT logical anchors (near = z {@link NEAR_Z}, far = z {@link FAR_Z})
 * onto their two real sprite screen anchors. This is what lets a script's `attacker
 * -> defender` span travel the full field — beams stretch from caster to target
 * instead of collapsing onto a shared midpoint (the old single-origin behaviour).
 */
export async function playShowdownAnim(
  scene: Phaser.Scene,
  anim: MoveAnim,
  actors: Actor[],
  opts: PlayShowdownOptions = {},
): Promise<void> {
  if (!scene || !scene.sys || !scene.sys.isActive()) return;

  const pace = opts.pace ?? 1;
  const catalog = opts.catalog ?? getFxCatalog();
  const depth = opts.depth ?? DEFAULT_DEPTH;
  const field = deriveFieldFromActors(actors);

  const run = new FxRun(scene);
  const fx = new FxScene(run, catalog, field, pace, depth);
  for (const a of actors) a.bind(run, field, pace);

  const finished = run.begin();

  try {
    if (opts.reduced) {
      playReduced(fx, actors);
    } else {
      anim(fx, actors);
    }
  } catch {
    // A throwing script (e.g. dead scene mid-call) must not break the loop.
  }
  // No more anims will be added: resolve once in-flight effects drain.
  run.seal();

  try {
    await finished;
  } finally {
    run.destroy();
  }
}

/**
 * Reduced-motion fallback: skip the full choreography, play a single quick, gentle
 * impact cue at the defender (or a soft flash at the attacker for self-targeting).
 * Uses the `impact` fx if present, else nothing visible — but still resolves cleanly.
 */
function playReduced(fx: FxScene, actors: Actor[]): void {
  const target = actors[1] ?? actors[0];
  if (!target) return;
  fx.showEffect(
    'impact',
    { x: target.x, y: target.y, z: target.z, scale: 0.4, opacity: 0 },
    { x: target.x, y: target.y, z: target.z, scale: 0.7, opacity: 0.9, time: 160 },
    'decel',
    'fade',
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * Field transform helpers
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Build a {@link FieldTransform} from a unit scale and the screen point PS's
 * (x=0,y=0,z=0) reference should map to. The integration layer will usually call
 * this with the midpoint between the two combatant sprites as the origin.
 */
export function makeFieldTransform(U: number, originX: number, originY: number): FieldTransform {
  return { U, originX, originY };
}

/**
 * Build an {@link Actor} anchored at a fixed SCREEN point, with logical x=y=z=0 (so it
 * resolves back to that screen anchor through the field transform) — the same shape
 * BattleScene's `makeActor` produces, but driven by a plain screen point instead of a
 * live combatant sprite. Used by callers outside battle (e.g. the Town tavern free-aim
 * cast) that want to fire a real Showdown move FROM one screen point TOWARD another.
 *
 * The returned Actor always carries a transform proxy (so it participates in
 * {@link deriveFieldFromActors}'s 2-point fit). Its logical z is {@link FAR_Z} when
 * `isFrontSprite` (the far/target side) and {@link NEAR_Z} otherwise (the near/caster
 * side), so an `attacker -> defender` span travels the full path between the two screen
 * anchors instead of collapsing onto one point. When `lunge` is a live transform, the
 * actor's `anim(...)` tweens move it (the caster); when omitted the proxy is read-only
 * (writes are no-ops) so a target point that has no creature to lunge still anchors
 * effects without moving anything. `active` always reports true so the engine's
 * ballistic onUpdate guards pass.
 *
 * @param anchor  the screen point this actor sits at (effects resolve onto it)
 * @param isFrontSprite  true = far/target side (flips PS facing signs); false = near/caster
 * @param lunge  an optional live transform whose x/y this actor's lunges drive (the caster)
 */
export function makeAnchoredActor(
  anchor: { x: number; y: number },
  isFrontSprite: boolean,
  lunge?: Phaser.GameObjects.Components.Transform | null,
): Actor {
  const baseX = anchor.x;
  const baseY = anchor.y;
  const proxy: Phaser.GameObjects.Components.Transform = {
    get x() {
      return lunge ? lunge.x : baseX;
    },
    set x(v: number) {
      if (lunge) lunge.x = v;
    },
    get y() {
      return lunge ? lunge.y : baseY;
    },
    set y(v: number) {
      if (lunge) lunge.y = v;
    },
    get active() {
      const l = lunge as unknown as { active?: boolean } | null | undefined;
      return l ? l.active !== false : true;
    },
  } as unknown as Phaser.GameObjects.Components.Transform;

  return new Actor(proxy, { x: 0, y: 0, z: isFrontSprite ? FAR_Z : NEAR_Z, isFrontSprite });
}

/**
 * Derive the field transform by FITTING the two combatants' DISTINCT logical anchors
 * onto their two real screen anchors (see {@link fitFieldToAnchors}). The near actor
 * carries logical z={@link NEAR_Z} and the far actor z={@link FAR_Z}, so the fit makes
 * a script's `attacker -> defender` span travel the FULL field — beams stretch from
 * caster to target instead of popping at the shared midpoint (the old behaviour, which
 * parked BOTH actors at one origin so any attacker→defender vector had zero length).
 *
 * The near/far roles are read from each actor's `isFrontSprite` (front = far/opponent).
 * Each actor's screen anchor is its sprite's current (x, y) — the body-centre proxy
 * BattleScene / the tavern cast wire in. With <2 sprited actors there is nothing to
 * span, so we keep the old single-origin behaviour at U=1.
 */
function deriveFieldFromActors(actors: Actor[]): FieldTransform {
  const sprited = actors.filter(
    (a): a is Actor & { sprite: Phaser.GameObjects.Components.Transform } => !!a.sprite,
  );
  if (sprited.length === 0) return { U: 1, originX: 0, originY: 0 };
  if (sprited.length === 1) {
    const s = sprited[0]!.sprite;
    return { U: 1, originX: s.x ?? 0, originY: s.y ?? 0 };
  }

  // Pick the near (player) and far (opponent) actors by facing. If both report the
  // same side (shouldn't happen in 1v1), fall back to array order [attacker, defender].
  const far = sprited.find((a) => a.isFrontSprite) ?? sprited[1]!;
  const near = sprited.find((a) => a !== far) ?? sprited[0]!;

  return fitFieldToAnchors(
    { x: near.sprite.x ?? 0, y: near.sprite.y ?? 0, z: NEAR_Z },
    { x: far.sprite.x ?? 0, y: far.sprite.y ?? 0, z: FAR_Z },
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * Small CSS colour parse for backgroundEffect (hex / rgb only — best-effort).
 * ──────────────────────────────────────────────────────────────────────── */

function parseCssColor(css: string): number {
  const s = css.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
  if (hex) {
    const h = hex[1];
    const full =
      h.length === 3
        ? h
            .split('')
            .map((c) => c + c)
            .join('')
        : h;
    return parseInt(full, 16);
  }
  const rgb = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(s);
  if (rgb) {
    return (parseInt(rgb[1], 10) << 16) | (parseInt(rgb[2], 10) << 8) | parseInt(rgb[3], 10);
  }
  // Unknown (gradients / named) — fall back to a neutral white wash.
  return 0xffffff;
}
